import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import CategoryManagementApi from '@src/apis/categoryManagement';
import { COLOR } from '@src/styles/color';
import { StyledCreateCategoryModal } from './modal.styles';

const CreateCategoryModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    translations: {
      vi: { name: '', description: '' },
      en: { name: '', description: '' },
      ko: { name: '', description: '' },
      zh: { name: '', description: '' },
    },
    order: 0,
    isActive: true,
  });

  const languages = [
    { code: 'vi', label: 'Tiếng Việt' },
    { code: 'en', label: 'Tiếng Anh' },
    { code: 'ko', label: 'Tiếng Hàn' },
    { code: 'zh', label: 'Tiếng Trung' },
  ];

  const handleInputChange = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));

    if (errors[lang]?.[field]) {
      const newErrors = { ...errors };
      if (newErrors[lang]) {
        delete newErrors[lang][field];
      }
      setErrors(newErrors);
    }
  };

  const handleOrderChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      order: parseInt(value, 10) || 0,
    }));
  };

  const handleStatusChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      isActive: checked,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.translations.vi?.name?.trim()) {
      newErrors.vi = { name: t('vietnameseNameRequired') };
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await CategoryManagementApi.createCategory(formData);

      if (response?.status) {
        enqueueSnackbar(t('createCategorySuccess'), { variant: 'success' });
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('createCategoryError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      translations: {
        vi: { name: '', description: '' },
        en: { name: '', description: '' },
        ko: { name: '', description: '' },
        zh: { name: '', description: '' },
      },
      order: 0,
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const renderLanguageForm = (lang) => (
    <Box key={lang.code} className="language-section">
      <Typography variant="h6" className="language-title">
        {lang.label}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography className="field-label">
            {t('categoryName')}{' '}
            {lang.code === 'vi' && <span className="required">*</span>}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formData.translations[lang.code]?.name || ''}
            onChange={(e) =>
              handleInputChange(lang.code, 'name', e.target.value)
            }
            error={!!errors[lang.code]?.name}
            helperText={errors[lang.code]?.name}
            placeholder={t('enterCategoryName')}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography className="field-label">{t('description')}</Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            value={formData.translations[lang.code]?.description || ''}
            onChange={(e) =>
              handleInputChange(lang.code, 'description', e.target.value)
            }
            placeholder={t('enterDescription')}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <StyledCreateCategoryModal open={open} onClose={handleClose} maxWidth="lg">
      <DialogTitle className="dialog-title">
        {t('createCategory')}
        <IconButton onClick={handleClose}>
          <FontAwesomeIcon
            icon={faXmark}
            color={COLOR.text.highlight}
            size="sm"
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content">
        {languages.map(renderLanguageForm)}

        <Box className="form-section">
          <Typography variant="h6" className="section-title">
            {t('settings')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">{t('order')}</Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.order}
                onChange={(e) => handleOrderChange(e.target.value)}
                placeholder={t('enterOrder')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={(e) => handleStatusChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('isActive')}
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="text">
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('creating') : t('create')}
        </Button>
      </DialogActions>
    </StyledCreateCategoryModal>
  );
};

export default CreateCategoryModal;
